'use client';

import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '@/lib/api-client-hooks';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { normalizeApiBaseUrl } from '@/lib/utils';
import { chatKeys } from '@/hooks/useChatApi';
import type { FileUploadPreviewResponse, ChatMessageListResponse } from '@/types/chat';

const API_BASE_URL = normalizeApiBaseUrl(getRuntimeConfig().apiBaseUrl || process.env.REACT_APP_API_BASE_URL || '');

interface UseUploadFileStreamReturn {
  isStreaming: boolean;
  statusMessage: string | null;
  streamedText: string;
  isCodeExecuting: boolean;
  error: string | null;
  usage: { input: number; output: number } | null;
  upload: (params: {
    userId: number;
    tenantId: number;
    sessionId: string;
    file: File;
  }) => Promise<FileUploadPreviewResponse | null>;
  abort: () => void;
}

/**
 * Hook for uploading an Excel file with SSE streaming analysis.
 *
 * Instead of waiting for the full analysis, this streams text deltas
 * and status events back to the caller in real-time.
 */
export function useUploadFileStream(): UseUploadFileStreamReturn {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useAuthenticatedFetch();
  const abortRef = useRef<AbortController | null>(null);
  const tenantIdRef = useRef<number | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  const [isStreaming, setIsStreaming] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [streamedText, setStreamedText] = useState('');
  const [isCodeExecuting, setIsCodeExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<{ input: number; output: number } | null>(null);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const upload = useCallback(
    async (params: { userId: number; tenantId: number; sessionId: string; file: File }) => {
      const { userId, tenantId, sessionId, file } = params;

      // Store in refs so SSE handler can access them
      tenantIdRef.current = tenantId;
      sessionIdRef.current = sessionId;

      // Reset state
      setIsStreaming(true);
      setStatusMessage(null);
      setStreamedText('');
      setIsCodeExecuting(false);
      setError(null);
      setUsage(null);

      const controller = new AbortController();
      abortRef.current = controller;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId.toString());

      let completeData: FileUploadPreviewResponse | null = null;

      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/backend/chat/sessions/${sessionId}/upload-stream`, {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Upload failed: ${response.status} - ${text}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Response body is not readable');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          // Keep last potentially incomplete line in buffer
          buffer = lines.pop() || '';

          let currentEvent: string | null = null;

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ') && currentEvent) {
              const dataStr = line.slice(6);
              try {
                const data = JSON.parse(dataStr);
                switch (currentEvent) {
                  case 'status':
                    setStatusMessage(data.message);
                    setIsCodeExecuting(false);
                    break;
                  case 'text_delta':
                    setStreamedText(prev => prev + data.text);
                    setStatusMessage(null);
                    setIsCodeExecuting(false);
                    break;
                  case 'code_executing':
                    setIsCodeExecuting(true);
                    setStatusMessage(data.message);
                    break;
                  case 'error':
                    setError(data.message);
                    setIsStreaming(false);
                    return null;
                  case 'complete':
                    completeData = data as FileUploadPreviewResponse;
                    // Capture usage
                    setUsage(data.usage ?? null);
                    // Immediately populate React Query cache with DB-persisted messages
                    // so they appear before the finally block clears streaming state
                    if (completeData.user_message && completeData.assistant_message && tenantIdRef.current) {
                      const key = chatKeys.messages(tenantIdRef.current, sessionIdRef.current!);
                      queryClient.setQueryData<ChatMessageListResponse>(key, old => ({
                        messages: [
                          ...(old?.messages ?? []).filter(m => !m.id.startsWith('optimistic-')),
                          completeData!.user_message,
                          completeData!.assistant_message,
                        ],
                        session_id: sessionIdRef.current!,
                        has_pending_file: completeData!.has_pending_file ?? false,
                        files: completeData!.files ?? old?.files,
                      }));
                    }
                    break;
                }
              } catch {
                // Ignore malformed JSON
              }
              currentEvent = null;
            } else if (line === '') {
              // Empty line resets event context
              currentEvent = null;
            }
          }
        }

        // Invalidate caches on success (will refetch, but cache already has data)
        if (completeData) {
          queryClient.invalidateQueries({
            queryKey: chatKeys.messages(tenantId, sessionId),
          });
          queryClient.invalidateQueries({
            queryKey: chatKeys.sessions(tenantId),
          });
        }

        return completeData;
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // User cancelled — not an error
          return null;
        }
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        return null;
      } finally {
        setIsStreaming(false);
        setIsCodeExecuting(false);
        setStatusMessage(null);
        abortRef.current = null;
      }
    },
    [fetchWithAuth, queryClient]
  );

  return { isStreaming, statusMessage, streamedText, isCodeExecuting, error, usage, upload, abort };
}
